import { Activity, Timer, Route, Mountain, Flame, CalendarRange, TrendingUp } from "lucide-react";
import { TOKENS } from "../../lib/designTokens";
import { getMetricVisual } from "../../lib/metricVisuals";
import { distanceUnit, distanceValue, fmtDurationShort } from "../../lib/format";
import { ProgressMetricCard } from "./ProgressMetricCard";
import { PROGRESS_METRIC_EXPLANATIONS } from "./progressMetricExplanations";

const SummaryGroup = ({ title, children, testId }) => (
  <div data-testid={testId}>
    <h4 className="card-label mb-3">{title}</h4>
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {children}
    </div>
  </div>
);

export const ProgressSummaryGrid = ({ summary, unitSystem }) => {
  const brandVisual = getMetricVisual("brand");
  const distanceVisual = getMetricVisual("distance");
  const durationVisual = getMetricVisual("duration");
  const elevationVisual = getMetricVisual("elevation");
  const caloriesVisual = getMetricVisual("calories");

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="progress-summary-cards">
      <SummaryGroup title="Volume" testId="progress-summary-volume">
        <ProgressMetricCard
          icon={brandVisual.icon}
          label="Activities"
          value={summary.activity_count}
          accent={brandVisual.accent}
          soft={brandVisual.soft}
          testId="progress-metric-activities"
        />
        <ProgressMetricCard
          icon={distanceVisual.icon}
          label="Distance"
          value={
            summary.total_distance_m != null
              ? distanceValue(summary.total_distance_m, unitSystem).toFixed(2)
              : null
          }
          unit={summary.total_distance_m != null ? distanceUnit(unitSystem) : null}
          accent={distanceVisual.accent}
          soft={distanceVisual.soft}
          testId="progress-metric-distance"
        />
        <ProgressMetricCard
          icon={durationVisual.icon}
          label="Moving Time"
          value={summary.total_moving_s != null ? fmtDurationShort(summary.total_moving_s) : null}
          accent={durationVisual.accent}
          soft={durationVisual.soft}
          testId="progress-metric-moving-time"
        />
        <ProgressMetricCard
          icon={durationVisual.icon}
          label="Elapsed Time"
          value={summary.total_elapsed_s != null ? fmtDurationShort(summary.total_elapsed_s) : null}
          accent={durationVisual.accent}
          soft={durationVisual.soft}
          testId="progress-metric-elapsed-time"
        />
        <ProgressMetricCard
          icon={elevationVisual.icon}
          label="Elevation Gain"
          value={
            summary.total_elevation_m != null
              ? Math.round(
                  unitSystem === "imperial" ? summary.total_elevation_m * 3.28084 : summary.total_elevation_m
                ).toLocaleString()
              : null
          }
          unit={summary.total_elevation_m != null ? (unitSystem === "imperial" ? "ft" : "m") : null}
          accent={elevationVisual.accent}
          soft={elevationVisual.soft}
          testId="progress-metric-elevation"
        />
        <ProgressMetricCard
          icon={caloriesVisual.icon}
          label="Calories"
          value={summary.total_calories != null ? Math.round(summary.total_calories).toLocaleString() : null}
          unit={summary.total_calories != null ? "kcal" : null}
          accent={caloriesVisual.accent}
          soft={caloriesVisual.soft}
          testId="progress-metric-calories"
        />
      </SummaryGroup>

      <SummaryGroup title="Consistency" testId="progress-summary-consistency">
        <ProgressMetricCard
          icon={Activity}
          label="Avg Activities / Week"
          value={summary.avg_activities_per_week != null ? summary.avg_activities_per_week.toFixed(2) : null}
          accent={TOKENS.analytics.blue}
          soft="rgba(47, 142, 234, 0.14)"
          testId="progress-metric-avg-activities"
        />
        <ProgressMetricCard
          icon={Timer}
          label="Avg Moving / Week"
          value={summary.avg_moving_s_per_week != null ? fmtDurationShort(summary.avg_moving_s_per_week) : null}
          accent={TOKENS.analytics.purple}
          soft="rgba(130, 105, 200, 0.14)"
          testId="progress-metric-avg-moving"
        />
        <ProgressMetricCard
          icon={CalendarRange}
          label="Active Weeks"
          value={summary.active_weeks != null ? summary.active_weeks : null}
          accent={TOKENS.analytics.green}
          soft="rgba(49, 185, 120, 0.14)"
          testId="progress-metric-active-weeks"
        />
        <ProgressMetricCard
          icon={TrendingUp}
          label="Training Consistency"
          value={
            summary.training_consistency_pct != null
              ? summary.training_consistency_pct.toFixed(1)
              : null
          }
          unit={summary.training_consistency_pct != null ? "%" : null}
          subtitle="Active weeks / total weeks"
          accent={TOKENS.analytics.green}
          soft="rgba(49, 185, 120, 0.14)"
          tooltip={{
            label: "Training Consistency",
            content: PROGRESS_METRIC_EXPLANATIONS.trainingConsistency,
            testId: "progress-tooltip-training-consistency",
          }}
          testId="progress-metric-training-consistency"
        />
      </SummaryGroup>

      <SummaryGroup title="Extremes" testId="progress-summary-extremes">
        <ProgressMetricCard
          icon={Route}
          label="Longest Distance"
          value={
            summary.longest_distance_activity?.distance_m != null
              ? distanceValue(summary.longest_distance_activity.distance_m, unitSystem).toFixed(2)
              : null
          }
          unit={
            summary.longest_distance_activity?.distance_m != null ? distanceUnit(unitSystem) : null
          }
          accent={distanceVisual.accent}
          soft={distanceVisual.soft}
          testId="progress-metric-longest-distance"
        />
        <ProgressMetricCard
          icon={Timer}
          label="Longest Moving Time"
          value={
            summary.longest_moving_time_activity?.moving_time_seconds != null
              ? fmtDurationShort(summary.longest_moving_time_activity.moving_time_seconds)
              : null
          }
          accent={durationVisual.accent}
          soft={durationVisual.soft}
          testId="progress-metric-longest-moving-time"
        />
      </SummaryGroup>
    </div>
  );
};

export { ProgressMetricCard as SummaryCard };
